import React, { useMemo } from 'react';
import Card from '../card/Card';
import P from '../texts/p/P';

export interface TotalsColumnDef {
  /** Field to sum from the installments array. null = empty cell. */
  field?: string | null;
  /** Custom computation for the total (overrides field sum). */
  computeTotal?: (rows: any[]) => number;
  /** Whether this is the label cell (shows "Totales"). */
  isLabel?: boolean;
  /** CSS min-width matching the DataTable column. */
  minWidth?: string;
  /** CSS flex-grow matching the DataTable column (default 1). */
  grow?: number;
  /** Fixed width (percentage or px) matching the DataTable column. */
  width?: string;
}

interface InstallmentTotalsFooterProps {
  installments: any[];
  columns: TotalsColumnDef[];
  label?: string;
}

const formatNumber = (value: number): string => {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const InstallmentTotalsFooter: React.FC<InstallmentTotalsFooterProps> = ({
  installments,
  columns,
  label = 'Totales',
}) => {
  const totals = useMemo(() => {
    if (!installments || installments.length === 0) return null;

    return columns.map((col) => {
      if (col.isLabel) return '__LABEL__';
      if (col.computeTotal) return col.computeTotal(installments);
      if (col.field) {
        return installments.reduce((sum, row) => sum + (Number(row[col.field!]) || 0), 0);
      }
      return null;
    });
  }, [installments, columns]);

  if (!totals) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minHeight: '48px',
        backgroundColor: '#F5F7FA',
        borderTop: '2px solid #DFE6EB',
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        pointerEvents: 'none',
        boxSizing: 'border-box',
      }}
      data-testid="installment-totals-footer"
    >
      {totals.map((total, idx) => {
        const col = columns[idx];
        const grow = col.grow ?? 1;
        const cellStyle: React.CSSProperties = {
          display: 'flex',
          alignItems: 'center',
          paddingLeft: '12px',
          paddingRight: '12px',
          height: '48px',
          ...(col.width
            ? { flex: `0 0 ${col.width}`, width: col.width }
            : { flex: `${grow} ${grow} 0px`, minWidth: col.minWidth || '100px' }),
        };

        if (total === '__LABEL__') {
          return (
            <div key={idx} style={cellStyle}>
              <Card>
                <P type='3' variant='neutral' weight='bold' withMargin={false}>
                  {label}
                </P>
              </Card>
            </div>
          );
        }

        return (
          <div key={idx} style={cellStyle}>
            {total !== null ? (
              <Card>
                <P type='3' variant='neutral' weight='bold' withMargin={false}>
                  {formatNumber(total as number)}
                </P>
              </Card>
            ) : (
              <Card>
                <P type='3' variant='neutral' withMargin={false}>
                  —
                </P>
              </Card>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default InstallmentTotalsFooter;
